import { UseDropdownItems } from "@/common/composition/dropdown-items";
import { UseToggle } from "@/common/composition/toggle-item";
import { UseCollection } from "@/common/composition/use-collection";
import { JustificationQueryParams, JustificationTableItem } from "./model";
import * as api from "./api";
import { computed, watch } from "@vue/composition-api";
import { SharedState } from "@/common/state";
import { UserStatus } from "@/views/admins/data/models";

const [
  event_types,
  selected_event_type,
  set_selected_event_type,
] = UseDropdownItems(
  [
    ["All", "All"],
    ["Email", "Email"],
    ["SecureLinkCreated", "Secure Link Created"],
    ["SecureLinkUpdated", "Secure Link Updated"],
    ["AnonymousLinkCreated", "Anonymous Link Created"],
    ["AnonymousLinkUpdated", "Anonymous Link Updated"],
    ["CompanyLinkCreated", "Company Link Created"],
    ["FileUploaded", "File Uploaded"],
    ["FileDownloaded", "File Downloaded"],
    ["FileSyncUploadedFull", "File Sync Uploaded Full"],
    ["SharingSet", "Sharing Set"],
  ].map(([id, name]) => {
    return {
      id,
      name,
    };
  })
);

const [
  admins,
  selected_admin,
  set_selected_admin,
  init_admins,
] = UseDropdownItems<{ id: string; name: string }>([
  { id: "All", name: "All" },
]);

const [show_pending_list, toggle_show_pending_list] = UseToggle(true);

const [
  applications,
  selected_application,
  set_application,
  init_applications,
] = UseDropdownItems(
  [["All", "All"]].map(([id, icon]) => {
    return {
      id,
      name: id,
      icon,
    };
  })
);

const [severities, selected_severity, set_severity] = UseDropdownItems(
  [
    ["All", "--dark"],
    //  ["Critical", "--level-h"],
    ["High", "--level-h"],
    ["Medium", "--level-m-bold"],
    ["Low", "--level-l"],
    ["None", "--gray-500"],
  ].map(([id, color]) => {
    return {
      id,
      name: id,
      color: `var(${color})`,
    };
  })
);

const justifications_filter_params = computed(() => {
  const params: JustificationQueryParams = {
    status: show_pending_list.value ? "Opened" : "Completed",
  };
  if (selected_severity.value?.id !== "All") {
    params.severity = selected_severity.value?.id;
  }
  if (selected_application.value?.id !== "All") {
    params.application = selected_application.value?.id;
  }
  if (selected_event_type.value?.id !== "All") {
    params.eventType = selected_event_type.value?.id;
  }
  if (selected_admin.value?.id !== "All") {
    params.issuer = selected_admin.value?.id;
  }

  return params;
});

watch(
  justifications_filter_params,
  () => {
    if (!justifications.is_loading.value) {
      justifications.clear();
      justifications.load_items({ cursor: "" });
    }
  },
  {
    lazy: true,
  }
);

const justifications = UseCollection<
  JustificationTableItem,
  { cursor: string }
>(async ({ cursor }) => {
  const res = await api.get_justifications({
    cursor,
    params: justifications_filter_params.value,
  });
  if (res.success) {
    return {
      success: true,
      value: {
        cursor: res.value.cursor,
        objects: res.value.values,
      },
    };
  } else {
    return res;
  }
});

const init = async () => {
  init_applications([
    applications.value[0],
    ...SharedState.data_sources.value.map((d) => ({
      id: d.id,
      name: d.name,
      icon: d.icon,
    })),
  ]);
  init_admins([
    { id: "All", name: "All" },
    ...Object.entries(SharedState.admins_with_status.value)
      .map(([id, { email, status }]) => {
        const admin = {
          id,
          name: email,
          desc: id === SharedState.COGNNI_ADMIN_ID ? "automation" : "",
        };
        if (status == UserStatus.BLOCKED) {
          // @ts-ignore
          admin.tag = {
            color: "level-h",
            name: "removed",
          };
        }
        return admin;
      })
      // @ts-ignore
      .sort((a, b) => a.name.localeCompare(b.name)),
  ]);
  console.log("admins:", admins.value);
  await justifications.load_items({
    cursor: "",
  });
};

export const state = {
  init,
  // events types
  event_types,
  selected_event_type,
  set_selected_event_type,
  // status
  show_pending_list,
  toggle_show_pending_list,
  // severities
  severities,
  set_severity,
  selected_severity,
  // applications
  applications,
  selected_application,
  set_application,
  // admins
  admins,
  selected_admin,
  set_selected_admin,
  // justifications
  justifications: justifications.items,
  is_loading: justifications.is_loading,
  has_no_results: justifications.has_no_results,
  load_more: justifications.load_more_items,
};
